import React, { useState, useEffect, useCallback } from "react";
import { 
  FaSearch, FaFilter, FaDownload, FaArrowUp, FaArrowDown, 
  FaBox, FaShoppingCart, FaTruck, FaExchangeAlt, FaUndo,
  FaCalendarAlt, FaTimes, FaChevronLeft, FaChevronRight,
  FaWarehouse, FaStore, FaGlobe, FaClipboardList, FaTrash,
  FaCheckCircle, FaTimesCircle, FaHistory
} from "react-icons/fa";
import { FiEdit2 } from "react-icons/fi";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { 
  getInventoryLogs, 
  getInventoryLogsSummary, 
  getLogFilterOptions,
  exportInventoryLogs,
  getLocalBranchNamesForInventory 
} from "../services/ClientService";
import { toast } from "react-toastify";

const InventoryLogs = () => {
  // State for logs data
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  // State for filters
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedLogType, setSelectedLogType] = useState(null);
  const [selectedSourceType, setSelectedSourceType] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // State for dropdown options
  const [branchOptions, setBranchOptions] = useState([]);
  const [logTypeOptions, setLogTypeOptions] = useState([]);
  const [sourceTypeOptions, setSourceTypeOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // State for summary
  const [summary, setSummary] = useState(null);
  const [showSummary, setShowSummary] = useState(true);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Fetch filter options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      setLoadingOptions(true);
      try {
        // Fetch branches
        const branchResponse = await getLocalBranchNamesForInventory({});
        if (branchResponse.code === 200 && branchResponse.success) {
          const branches = branchResponse.data.map(branch => ({
            value: branch._id,
            label: branch.masterAccount ? `${branch.branchName} (Warehouse)` : branch.branchName,
            ...branch
          }));
          branches.unshift({ value: 'all', label: 'All Branches' });
          setBranchOptions(branches);
        }

        // Fetch log type and source type options
        const filterResponse = await getLogFilterOptions({});
        if (filterResponse.code === 200 && filterResponse.success) {
          setLogTypeOptions([
            { value: null, label: 'All Types' },
            ...filterResponse.data.logTypes.map(t => ({ value: t.value, label: t.label, color: t.color }))
          ]);
          setSourceTypeOptions([
            { value: null, label: 'All Sources' },
            ...filterResponse.data.sourceTypes.map(s => ({ value: s.value, label: s.label, icon: s.icon }))
          ]);
        }
      } catch (error) {
        console.error("Error fetching filter options:", error);
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, []);

  // Fetch logs
  const fetchLogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const skip = (page - 1) * pagination.limit;
      const params = {
        branchId: selectedBranch?.value || 'all',
        logType: selectedLogType?.value || null,
        sourceType: selectedSourceType?.value || null,
        searchTerm: debouncedSearch,
        startDate: startDate ? startDate.toISOString() : null,
        endDate: endDate ? endDate.toISOString() : null,
        skip,
        limit: pagination.limit
      };

      const response = await getInventoryLogs(params);
      if (response.code === 200 && response.success) {
        setLogs(response.data);
        const totalPages = response.pagination.totalPages;
        setPagination(prev => ({
          ...prev,
          currentPage: page,
          totalCount: response.pagination.totalCount,
          totalPages: totalPages,
          hasNextPage: page < totalPages,
          hasPrevPage: page > 1
        }));
      }
    } catch (error) {
      console.error("Error fetching inventory logs:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, selectedLogType, selectedSourceType, debouncedSearch, startDate, endDate, pagination.limit]);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    try {
      const params = {
        branchId: selectedBranch?.value || 'all',
        startDate: startDate ? startDate.toISOString() : null,
        endDate: endDate ? endDate.toISOString() : null
      };

      const response = await getInventoryLogsSummary(params);
      if (response.code === 200 && response.success) {
        setSummary(response.data);
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
    }
  }, [selectedBranch, startDate, endDate]);

  // Fetch on filter change
  useEffect(() => {
    fetchLogs(1);
    fetchSummary();
  }, [fetchLogs, fetchSummary]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchLogs(newPage);
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      const params = {
        branchId: selectedBranch?.value || 'all',
        logType: selectedLogType?.value || null,
        sourceType: selectedSourceType?.value || null,
        searchTerm: debouncedSearch,
        startDate: startDate ? startDate.toISOString() : null,
        endDate: endDate ? endDate.toISOString() : null
      };

      const response = await exportInventoryLogs(params);
      
      // Create blob and download
      const blob = new Blob([response], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Inventory_Logs_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting logs:", error);
      toast.error("Failed to export logs. Please try again.");
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setSelectedBranch(null);
    setSelectedLogType(null);
    setSelectedSourceType(null);
    setStartDate(null);
    setEndDate(null);
  };

  // Get icon for source type
  const getSourceIcon = (sourceType) => {
    const icons = {
      'PURCHASE_ORDER': <FaBox className="text-blue-500" />,
      'MANUAL_UPDATE': <FiEdit2 className="text-gray-500" />,
      'DISTRIBUTION_SENT': <FaTruck className="text-purple-500" />,
      'DISTRIBUTION_RECEIVED': <FaWarehouse className="text-indigo-500" />,
      'ONLINE_ORDER': <FaGlobe className="text-green-500" />,
      'OFFLINE_ORDER': <FaStore className="text-orange-500" />,
      'MARKETPLACE_ORDER': <FaStore className="text-violet-500" />,
      'ORDER_CANCELLED': <FaUndo className="text-yellow-500" />,
      'ORDER_CANCELLED_BY_CUSTOMER': <FaUndo className="text-orange-400" />,
      'ORDER_CANCELLED_BY_RETAILER': <FaUndo className="text-red-400" />,
      'ORDER_TRANSFER_OUT': <FaExchangeAlt className="text-cyan-500" />,
      'ORDER_TRANSFER_IN': <FaExchangeAlt className="text-teal-500" />,
      'DAMAGE_EXPIRED': <FaTrash className="text-red-500" />,
      'RETURN': <FaUndo className="text-green-600" />,
      'INITIAL_STOCK': <FaClipboardList className="text-blue-600" />,
      'BULK_DISTRIBUTION': <FaTruck className="text-purple-600" />,
      'APPROVAL_DISTRIBUTION': <FaCheckCircle className="text-green-500" />
    };
    return icons[sourceType] || <FaHistory className="text-gray-400" />;
  };

  // Format source type for display
  const formatSourceType = (sourceType) => {
    const labels = {
      'PURCHASE_ORDER': 'Purchase Order',
      'MANUAL_UPDATE': 'Manual Update',
      'DISTRIBUTION_SENT': 'Distribution Sent',
      'DISTRIBUTION_RECEIVED': 'Distribution Received',
      'ONLINE_ORDER': 'Online Order',
      'OFFLINE_ORDER': 'Walk-in Order',
      'MARKETPLACE_ORDER': 'Marketplace Order',
      'ORDER_CANCELLED': 'Order Cancelled',
      'ORDER_CANCELLED_BY_CUSTOMER': 'Cancelled by Customer',
      'ORDER_CANCELLED_BY_RETAILER': 'Cancelled by Retailer',
      'ORDER_TRANSFER_OUT': 'Transfer Out',
      'ORDER_TRANSFER_IN': 'Transfer In',
      'DAMAGE_EXPIRED': 'Damaged/Expired',
      'RETURN': 'Customer Return',
      'INITIAL_STOCK': 'Initial Stock',
      'BULK_DISTRIBUTION': 'Bulk Distribution',
      'APPROVAL_DISTRIBUTION': 'Approval Distribution'
    };
    return labels[sourceType] || sourceType;
  };

  // One clean Reference cell — no duplicate em-dash + From/To lines
  const renderReference = (log) => {
    const related = log.context?.relatedBranchName;
    const code = log.reference?.code;
    const orderId = log.context?.orderId;

    if (log.sourceType === 'DISTRIBUTION_SENT' && related) {
      return <span className="text-xs text-gray-700">To <span className="font-medium">{related}</span>{code ? <span className="block text-[10px] text-indigo-600 font-mono mt-0.5">{code}</span> : null}</span>;
    }
    if (log.sourceType === 'DISTRIBUTION_RECEIVED' && related) {
      return <span className="text-xs text-gray-700">From <span className="font-medium">{related}</span>{code ? <span className="block text-[10px] text-indigo-600 font-mono mt-0.5">{code}</span> : null}</span>;
    }
    if (log.sourceType === 'ORDER_TRANSFER_OUT' && related) {
      return <span className="text-xs text-gray-700">To <span className="font-medium">{related}</span></span>;
    }
    if (log.sourceType === 'ORDER_TRANSFER_IN' && related) {
      return <span className="text-xs text-gray-700">From <span className="font-medium">{related}</span></span>;
    }
    if (log.sourceType === 'DAMAGE_EXPIRED') {
      const notes = log.context?.notes;
      if (code) {
        return (
          <span className="text-xs text-gray-700">
            <span className="bg-red-50 text-red-700 px-2 py-1 rounded font-mono">{code}</span>
            {notes ? (
              <span className="block text-[10px] text-gray-500 mt-0.5 max-w-[200px] truncate" title={notes}>
                {notes}
              </span>
            ) : null}
          </span>
        );
      }
      if (notes) {
        return (
          <span className="text-xs text-gray-600 max-w-[220px] truncate block" title={notes}>
            {notes}
          </span>
        );
      }
      return <span className="text-xs text-gray-500">Write-off</span>;
    }
    if (code) {
      return <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded font-mono">{code}</span>;
    }
    if (orderId) {
      return <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded font-mono">{orderId}</span>;
    }
    if (log.sourceType === 'MANUAL_UPDATE' || log.reference?.type === 'MANUAL') {
      return <span className="text-xs text-gray-500">Manual update</span>;
    }
    if (related) {
      return <span className="text-xs text-gray-600">{related}</span>;
    }
    return <span className="text-xs text-gray-400">—</span>;
  };

  // Format numeric quantities reliably with 2 decimals
  const formatQuantity = (value) => {
    if (value === null || value === undefined) return '—';
    const num = parseFloat(value);
    if (isNaN(num)) return String(value);
    return num.toFixed(2);
  };

  // Only apply decimal formatting for common inventory; otherwise show rounded integer
  const formatIfCommon = (value, isCommon) => {
    if (value === null || value === undefined) return '—';
    if (isCommon) return formatQuantity(value);
    const num = Number(value);
    if (isNaN(num)) return String(value);
    return Math.round(num).toString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-20">
        <div className="px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <FaHistory className="text-2xl text-indigo-600" />
              <h1 className="text-2xl font-bold text-gray-800">Inventory Logs</h1>
              <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-medium">
                {pagination.totalCount.toLocaleString()} Records
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative flex-grow min-w-[200px] max-w-md">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products, orders, branches..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  showFilters 
                    ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-500' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <FaFilter />
                Filters
                {(selectedBranch || selectedLogType || selectedSourceType || startDate || endDate) && (
                  <span className="bg-indigo-500 text-white text-xs px-2 py-0.5 rounded-full">
                    Active
                  </span>
                )}
              </button>

              {/* Export Button */}
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-all"
              >
                <FaDownload />
                Export
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex flex-wrap items-end gap-4">
                {/* Branch Filter */}
                <div className="min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                  <Select
                    options={branchOptions}
                    value={selectedBranch}
                    onChange={setSelectedBranch}
                    placeholder="All Branches"
                    isClearable
                    isLoading={loadingOptions}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>

                {/* Log Type Filter */}
                <div className="min-w-[150px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <Select
                    options={logTypeOptions}
                    value={selectedLogType}
                    onChange={setSelectedLogType}
                    placeholder="All Types"
                    isClearable
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>

                {/* Source Type Filter */}
                <div className="min-w-[180px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <Select
                    options={sourceTypeOptions}
                    value={selectedSourceType}
                    onChange={setSelectedSourceType}
                    placeholder="All Sources"
                    isClearable
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>

                {/* Date Range */}
                <div className="min-w-[150px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                  <DatePicker
                    selected={startDate}
                    onChange={setStartDate}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Start Date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    isClearable
                  />
                </div>

                <div className="min-w-[150px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                  <DatePicker
                    selected={endDate}
                    onChange={setEndDate}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="End Date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    isClearable
                    minDate={startDate}
                  />
                </div>

                {/* Clear Filters */}
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-all"
                >
                  <FaTimes />
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {showSummary && summary && (
        <div className="px-6 py-4 bg-white border-b">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {/* Stock In Summary */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <FaArrowUp className="text-green-600" />
                <span className="text-sm font-medium text-green-700">Stock In</span>
              </div>
              <p className="text-2xl font-bold text-green-800">
                {summary.byType?.STOCK_IN?.count || 0}
              </p>
              <p className="text-xs text-green-600 mt-1">
                {[
                  summary.byType?.STOCK_IN?.regularUnits > 0 ? `+${(summary.byType?.STOCK_IN?.regularUnits || 0).toLocaleString()} units` : null,
                  ...(summary.commonInventory?.stockIn?.map(item => `${item.quantity.toLocaleString()} ${item.metric}`) || [])
                ].filter(Boolean).join(', ') || '+0 units'}
              </p>
            </div>

            {/* Stock Out Summary */}
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border border-red-200">
              <div className="flex items-center gap-2 mb-2">
                <FaArrowDown className="text-red-600" />
                <span className="text-sm font-medium text-red-700">Stock Out</span>
              </div>
              <p className="text-2xl font-bold text-red-800">
                {summary.byType?.STOCK_OUT?.count || 0}
              </p>
              <p className="text-xs text-red-600 mt-1">
                {[
                  summary.byType?.STOCK_OUT?.regularUnits > 0 ? `-${(summary.byType?.STOCK_OUT?.regularUnits || 0).toLocaleString()} units` : null,
                  ...(summary.commonInventory?.stockOut?.map(item => `${item.quantity.toLocaleString()} ${item.metric}`) || [])
                ].filter(Boolean).join(', ') || '-0 units'}
              </p>
            </div>

            {/* Online Orders */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <FaGlobe className="text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Online Orders</span>
              </div>
              <p className="text-2xl font-bold text-blue-800">
                {summary.bySource?.find(s => s._id === 'ONLINE_ORDER')?.count || 0}
              </p>
            </div>

            {/* Walk-in Orders */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
              <div className="flex items-center gap-2 mb-2">
                <FaStore className="text-orange-600" />
                <span className="text-sm font-medium text-orange-700">Walk-in Orders</span>
              </div>
              <p className="text-2xl font-bold text-orange-800">
                {summary.bySource?.find(s => s._id === 'OFFLINE_ORDER')?.count || 0}
              </p>
            </div>

            {/* Purchase Orders */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <FaBox className="text-purple-600" />
                <span className="text-sm font-medium text-purple-700">Purchase Orders</span>
              </div>
              <p className="text-2xl font-bold text-purple-800">
                {summary.bySource?.find(s => s._id === 'PURCHASE_ORDER')?.count || 0}
              </p>
            </div>

            {/* Recent Activity */}
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-center gap-2 mb-2">
                <FaHistory className="text-indigo-600" />
                <span className="text-sm font-medium text-indigo-700">Last 24 Hours</span>
              </div>
              <p className="text-2xl font-bold text-indigo-800">
                {summary.recentActivityCount || 0}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Logs Table */}
      <div className="px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center min-h-[90vh]">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500">
            <FaHistory className="text-6xl mb-4 text-gray-300" />
            <p className="text-xl font-medium">No inventory logs found</p>
            <p className="text-sm mt-2">Try adjusting your filters or date range</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Date & Time</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Branch</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Product</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Source</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Change</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Previous</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">New</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Reference</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log, index) => (
                  <tr key={log._id || index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {new Date(log.createdAt).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(log.createdAt).toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{log.branchName || 'N/A'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900 max-w-[200px] truncate" title={log.productName}>
                        {log.productName}
                      </div>
                      {log.optionLabel && (
                        <span className="inline-block mt-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {log.optionLabel}
                        </span>
                      )}
                      {log.isCommonInventory && (
                        <span className="inline-block mt-1 ml-1 text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded">
                          Common ({log.metricLabel || 'Kg'})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                        log.logType === 'STOCK_IN' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {log.logType === 'STOCK_IN' ? <FaArrowUp /> : <FaArrowDown />}
                        {log.logType === 'STOCK_IN' ? 'In' : 'Out'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getSourceIcon(log.sourceType)}
                        <span className="text-sm text-gray-700">
                          {formatSourceType(log.sourceType)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm font-bold ${
                        log.quantityChange >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {log.quantityChange >= 0 ? '+' : ''}{log.quantityChange}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm text-gray-500">{formatIfCommon(log.previousQuantity, log.isCommonInventory)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-medium text-gray-900">{formatIfCommon(log.newQuantity, log.isCommonInventory)}</span>
                    </td>
                    <td className="px-4 py-3">
                      {renderReference(log)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="mt-4 flex flex-col md:flex-row items-center justify-between gap-4 px-4 py-3 bg-white rounded-lg shadow-sm border">
            <div className="text-sm text-gray-600">
              Showing page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalCount} total records)
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={!pagination.hasPrevPage}
                className="px-3 py-2 bg-white border border-gray-300 rounded-lg font-medium text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                First
              </button>

              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrevPage}
                className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-300 rounded-lg font-medium text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <FaChevronLeft className="text-xs" />
                Previous
              </button>

              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.currentPage >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                        pagination.currentPage === pageNum
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNextPage}
                className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-300 rounded-lg font-medium text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Next
                <FaChevronRight className="text-xs" />
              </button>

              <button
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={!pagination.hasNextPage}
                className="px-3 py-2 bg-white border border-gray-300 rounded-lg font-medium text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryLogs;
